<template>
	<ModalBox
		v-model="isOpen"
		size="md"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
	>
		<div class="px-6 pt-6">
			<h3 class="mb-0 text-lg font-semibold text-surface-text-2">Export CSV</h3>
			<p class="mb-0 mt-1 text-sm text-surface-8">
				Exports the data already on screen for {{ rangeLabel }}. Times use {{ timezone }}. Durations are h:mm:ss.
			</p>
		</div>
		<div class="flex flex-col gap-3 px-6 py-5">
			<label v-for="section in sections" :key="section.key" class="flex cursor-pointer items-start gap-3 rounded-lg bg-surface-3/40 px-3 py-2.5 hover:bg-surface-3/70" :class="{ 'opacity-50': !section.available }">
				<FormCheckbox v-model="selected" :input-value="section.key" :disabled="!section.available" name="analytics-csv-sections" class="mt-0.5" />
				<span class="min-w-0">
					<span class="block text-sm text-surface-text-1">{{ section.label }}</span>
					<span class="block text-xs text-surface-7">{{ section.available ? section.description : section.unavailableText }}</span>
				</span>
			</label>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 border-t border-surface-3 px-6 py-4">
				<FormButton type="secondary" size="md" @click="isOpen = false">Cancel</FormButton>
				<FormButton type="main" size="md" :disabled="!selected.length" @click="onExport">Download CSV</FormButton>
			</div>
		</template>
	</ModalBox>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ModalBox from '@/components/modals/ModalBox.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	rangeLabel: { type: String, default: 'the selected range' },
	timezone: { type: String, default: 'UTC' },
	trendMetricLabel: { type: String, default: 'Plays' },
	hasTrend: { type: Boolean, default: false },
	hasLeaderboard: { type: Boolean, default: false },
	hasCompare: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'export']);

const isOpen = computed({
	get: () => props.modelValue,
	set: (v) => emit('update:modelValue', v),
});

const sections = computed(() => [
	{
		key: 'kpis',
		label: 'Summary',
		description: props.hasCompare ? 'One row per metric with the current and previous period.' : 'One row per metric for the selected period.',
		available: true,
	},
	{
		key: 'trend',
		label: `Trend (${props.trendMetricLabel})`,
		description: 'One row per time bucket for the metric currently shown in the chart.',
		unavailableText: 'The trend has not loaded for this period.',
		available: props.hasTrend,
	},
	{
		key: 'leaderboard',
		label: 'Top content',
		description: 'One row per item with plays, unique viewers, watch time, average view time and peak viewers.',
		unavailableText: 'Top content is hidden while a single item is selected.',
		available: props.hasLeaderboard,
	},
]);

const selected = ref([]);

watch(() => props.modelValue, (open) => {
	if (open) selected.value = sections.value.filter((s) => s.available).map((s) => s.key);
});

function onExport() {
	if (!selected.value.length) return;
	emit('export', selected.value.slice());
	isOpen.value = false;
}
</script>
